import { z } from 'zod'

import {
  PrinterConnectionType,
  PrinterManufacturer,
  PrinterModel,
  PrinterPaperWidth,
  PrinterPrintType,
} from '../constants/printer'
import { dbEntitySchema, listQueryZodFields } from './system'

export const printerSettingsSchema = z.object({
  paperWidth: z.nativeEnum(PrinterPaperWidth).default(PrinterPaperWidth.MM_80),
  charactersPerLine: z.number().int().positive().optional().nullable(),
  autoCut: z.boolean().default(true),
  copies: z.number().int().min(1).max(5).default(1),
  printTypes: z.array(z.nativeEnum(PrinterPrintType)).default([]),
})

export const printerStatusSchema = z.object({
  lastSeenAt: z.date().optional().nullable(),
  lastPrintedAt: z.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
})

/** Client-safe derived status (online is computed, never trusted from DB alone). */
export const printerStatusClientSchema = printerStatusSchema.extend({
  online: z.boolean(),
})

export const printerSchema = dbEntitySchema.extend({
  organization: z.string(),
  venue: z.string(),
  name: z.string(),
  autoPrint: z.boolean().optional().nullable(),
  type: z.string().optional().nullable(),
  connectionType: z
    .nativeEnum(PrinterConnectionType)
    .default(PrinterConnectionType.STAR_CLOUDPRNT),
  manufacturer: z.nativeEnum(PrinterManufacturer).optional().nullable(),
  model: z.nativeEnum(PrinterModel).optional().nullable(),
  deviceId: z.string().optional().nullable(),
  enabled: z.boolean().default(true),
  settings: printerSettingsSchema.optional().nullable(),
  status: printerStatusSchema.optional().nullable(),
})

/** API response: never includes credentials. */
export const printerClientSchema = printerSchema.omit({ status: true }).extend({
  status: printerStatusClientSchema.optional().nullable(),
  digestConfigured: z.boolean(),
})

export const printerCreateArgsSchema = z
  .object({
    venueId: z.string(),
    name: z.string().min(1),
    autoPrint: z.boolean().optional(),
    type: z.string().optional().nullable(),
    connectionType: z
      .nativeEnum(PrinterConnectionType)
      .default(PrinterConnectionType.STAR_CLOUDPRNT),
    manufacturer: z.nativeEnum(PrinterManufacturer).optional().nullable(),
    model: z.nativeEnum(PrinterModel).optional().nullable(),
    deviceId: z
      .string()
      .min(1)
      .max(100)
      .regex(/^[A-Za-z0-9._-]+$/, {
        message:
          'deviceId must be alphanumeric with . _ - only (Epson WebConfig ID)',
      })
      .optional()
      .nullable(),
    digestPassword: z.string().min(1).max(100).optional().nullable(),
    enabled: z.boolean().optional(),
    settings: printerSettingsSchema.partial().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.connectionType === PrinterConnectionType.EPSON_SDP) {
      if (!data.deviceId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['deviceId'],
          message: 'deviceId is required for EPSON_SDP printers',
        })
      }
      if (!data.digestPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['digestPassword'],
          message: 'digestPassword is required for EPSON_SDP printers',
        })
      }
      if (!data.manufacturer) {
        data.manufacturer = PrinterManufacturer.EPSON
      }
      if (!data.model) {
        data.model = PrinterModel.TM_M30III
      }
    }
  })

export const printerUpdateByIdArgsSchema = z.object({
  name: z.string().min(1).optional(),
  autoPrint: z.boolean().optional(),
  type: z.string().optional().nullable(),
  manufacturer: z.nativeEnum(PrinterManufacturer).optional().nullable(),
  model: z.nativeEnum(PrinterModel).optional().nullable(),
  deviceId: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9._-]+$/)
    .optional()
    .nullable(),
  digestPassword: z.string().min(1).max(100).optional().nullable(),
  enabled: z.boolean().optional(),
  settings: printerSettingsSchema.partial().optional().nullable(),
})

export const printerListQuerySchema = z.object({
  ...listQueryZodFields,
  venueId: z.string().optional().nullable(),
  connectionType: z.nativeEnum(PrinterConnectionType).optional().nullable(),
})

export const printerListResponseSchema = z.object({
  data: z.array(printerClientSchema),
})

export const printerTestPrintResponseSchema = z.object({
  id: z.string(),
  status: z.string(),
  type: z.string(),
  printer: z.string(),
  venue: z.string(),
})
